import {
    buildAgencyDigest,
    IAgencyClient,
    IAgencyFinding,
    toAgencyFinding,
    asUnscored,
    buildClientWellbeing,
    buildWeeklyStandings,
    CaseloadReading,
    DOMAIN_LABEL,
    IClientWellbeing,
    IWeeklyStanding,
    NO_SCORE_SENTENCE,
    READING_LABEL,
    UNPLACED_INCIDENT_SENTENCE,
    comparableClientIds
} from '@innerhive-internal/innerhive-common';
import { IInsight } from '../../insights/IInsight';

/**
 * One care recipient's status, built from the same arithmetic the agency tab uses.
 *
 * Deliberately the same functions rather than a second implementation, so the same findings always
 * produce the same score wherever they are read. That is as far as the guarantee goes: an insight is
 * dismissed per reader, so a family member who marked one "not useful" is scoring a smaller set than
 * the agency sees, and a viewer without private access is handed no insights at all. Same arithmetic
 * over whatever that reader can see, never the same number for everybody.
 *
 * The rule the agency tab is built on holds here too: this describes what is on the record. It never
 * says why something changed, and it never tells anybody what to do about it beyond looking.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/** The insights this reader can see, in the shape the scoring understands. */
export const toClientFindings = (params: {
    careMapId: string;
    clientName: string;
    insights: IInsight[];
}): IAgencyFinding[] => {
    const { careMapId, clientName, insights } = params;

    return insights.map(insight => toAgencyFinding({ careMapId, clientName, insight }));
};

export interface IClientStatus {
    wellbeing: IClientWellbeing;
    /** How they read on the pill under the diagram: the same reading an agency's caseload map draws them in. */
    reading: CaseloadReading;
    /** Rolling seven-day spans across the recent window, oldest first. */
    weeks: IWeeklyStanding[];
    /** Observations recorded in the last seven days, and in the seven before them. */
    thisWeekCount: number;
    lastWeekCount: number;
    /** And across the last four weeks against the four before them. */
    thisMonthCount: number;
    lastMonthCount: number;
}

const countBetween = (params: { findings: IAgencyFinding[]; from: number; to: number }): number => {
    const { findings, from, to } = params;
    return findings.filter(finding => {
        const at = new Date(finding.observedOn).getTime();
        return !isNaN(at) && at >= from && at < to;
    }).length;
};

export const buildClientStatus = (params: {
    careMapId: string;
    clientName: string;
    findings: IAgencyFinding[];
    now: Date;
}): IClientStatus => {
    const { careMapId, clientName, findings, now } = params;

    // A caseload of one. Coverage is not this page's question, so the flags say nothing is missing
    // rather than inventing an answer the care profile already gives properly.
    const client: IAgencyClient = {
        careMapId,
        clientName,
        coverageKnown: true,
        hasConfirmedCodeStatus: true,
        hasInsuranceOnFile: true,
        hasIdentityDocument: true
    };

    const digest = buildAgencyDigest({ findings, clients: [client], now });
    const at = now.getTime();

    const wellbeing = buildClientWellbeing({
        careMapId,
        clientName,
        findings: digest.recentFindings,
        since: new Date(`${digest.recentStartsOn}T00:00:00.000Z`)
    });
    // The agency tab's rule, read off the same one-client digest. This page has no last-recorded
    // date, so recent weeks with no finding at all read as nothing recent on record.
    const comparable = comparableClientIds(digest).has(careMapId);
    const recordedRecently = digest.baselines[0].volume.recordedRecently;

    return {
        wellbeing: comparable ? wellbeing : asUnscored({ wellbeing, recordedRecently }),
        reading: digest.baselines[0].reading,
        weeks: buildWeeklyStandings({ digest, careMapId, clientName }),
        // `at + 1` so a finding stamped exactly now falls inside the window rather than just past
        // its exclusive end, which is otherwise a whole observation lost on the day it is recorded.
        thisWeekCount: countBetween({ findings: digest.windowFindings, from: at - WEEK_MS, to: at + 1 }),
        lastWeekCount: countBetween({ findings: digest.windowFindings, from: at - 2 * WEEK_MS, to: at - WEEK_MS }),
        thisMonthCount: countBetween({ findings: digest.windowFindings, from: at - 4 * WEEK_MS, to: at + 1 }),
        lastMonthCount: countBetween({ findings: digest.windowFindings, from: at - 8 * WEEK_MS, to: at - 4 * WEEK_MS })
    };
};

const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);

const describeCount = (count: number): string =>
    `${count} ${plural(count, 'observation', 'observations')}`;

/** How this week compares with last, in Bea's voice. */
export const describeWeekOnWeek = (status: IClientStatus): string => {
    const { thisWeekCount, lastWeekCount } = status;

    if (thisWeekCount === 0 && lastWeekCount === 0) {
        return 'Nothing has been written down in the last two weeks.';
    }

    if (thisWeekCount === 0) {
        return `Nothing this week, after ${describeCount(lastWeekCount)} last week.`;
    }

    if (lastWeekCount === 0) {
        return `${describeCount(thisWeekCount)} this week, after a quiet one before it.`;
    }

    const change = thisWeekCount - lastWeekCount;
    if (change === 0) {
        return `${describeCount(thisWeekCount)} this week, the same as last week.`;
    }

    return change > 0
        ? `${describeCount(thisWeekCount)} this week, up from ${lastWeekCount} last week.`
        : `${describeCount(thisWeekCount)} this week, down from ${lastWeekCount} last week.`;
};

/** And the last four weeks against the four before them, which is where a real direction shows. */
export const describeMonthOnMonth = (status: IClientStatus): string => {
    const { thisMonthCount, lastMonthCount } = status;

    if (thisMonthCount === 0 && lastMonthCount === 0) {
        return 'Nothing on the record either month, so there is no direction for me to read yet.';
    }

    if (lastMonthCount === 0) {
        return `${describeCount(thisMonthCount)} in the last four weeks. There is nothing before that to compare against yet, so I will have more to say next month.`;
    }

    const change = thisMonthCount - lastMonthCount;
    if (change === 0) {
        return `${describeCount(thisMonthCount)} in the last four weeks, level with the four before them.`;
    }

    // A month is where a direction becomes worth naming. A single busy week is noise on a record this
    // sparse, which is why the week-on-week line above never calls anything a trend.
    return change > 0
        ? `${describeCount(thisMonthCount)} in the last four weeks, up from ${lastMonthCount} in the four before. More is being noticed, which is not the same as more going wrong.`
        : `${describeCount(thisMonthCount)} in the last four weeks, down from ${lastMonthCount} in the four before. Worth checking that people are still writing things down before reading it as everything settling.`;
};

/** Where they are right now, and what has moved. */
export const describeStatusNow = (status: IClientStatus): string => {
    const { wellbeing, reading } = status;

    if (wellbeing.overall === 'unscored') {
        return NO_SCORE_SENTENCE;
    }

    const headline = `${READING_LABEL[reading]}, at ${wellbeing.score} out of 100.`;
    const hasUnplaced = wellbeing.unplacedIncidents.length > 0;
    if (wellbeing.moved.length === 0) {
        return hasUnplaced
            ? `${headline} ${UNPLACED_INCIDENT_SENTENCE}`
            : `Everything is sitting where it usually does, which puts them at ${wellbeing.score} out of 100. Nothing has been raised about any part of daily life recently.`;
    }

    const named = wellbeing.moved.slice(0, 2).map(domain => DOMAIN_LABEL[domain].toLowerCase());
    const list = named.length === 2 ? `${named[0]} and ${named[1]}` : named[0];
    const more = wellbeing.moved.length - named.length;
    const tail = more > 0 ? `, and ${more} more` : '';

    const moved = `${list.charAt(0).toUpperCase()}${list.slice(1)}${tail} ${plural(wellbeing.moved.length, 'has', 'have')} moved against their own baseline.`;
    return hasUnplaced ? `${headline} ${moved} ${UNPLACED_INCIDENT_SENTENCE}` : `${headline} ${moved}`;
};

/** Whether the last seven days are better, worse or the same as the seven before. */
export const describeDirection = (status: IClientStatus): string => {
    // Always four spans, from `RECENT_WEEKS`, so both weeks below are present. Narrowing that
    // constant to one would leave `before` undefined and take this line out with it.
    const { weeks } = status;
    const now = weeks[weeks.length - 1];
    const before = weeks[weeks.length - 2];

    if (now.score === before.score) {
        return 'Holding level with last week.';
    }

    return now.score > before.score
        ? `Better than last week, up from ${before.score} to ${now.score}.`
        : `Down from ${before.score} last week to ${now.score} this week.`;
};
